import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAnalyticsPage } from "../context/AnalyticsPageContext";
import { useRegisterAnalyticsTabExport } from "../context/AnalyticsTabExportContext";
import { useRevenueAnalytics } from "../hooks/useRevenueAnalytics";
import { analyticsRevenueService } from "@/services/analytics-revenue-service";
import { downloadBlob } from "@/utils/downloadFile";
import { REVENUE_COLORS } from "./revenue/constants";
import {
  // Future: Revenue by channel / Trend by channel
  // mapRevenueByChannelSegments,
  // mapRevenueByServiceSeries,
  mapRevenueDistributionSegments,
  mapRevenueKpiMetrics,
  mapRevenueSeasonalitySeries,
} from "./revenue/revenueMappers";
import {
  collectRevenueCurrencies,
  formatRevenueCenterLabel,
  resolveRevenueCurrency,
} from "./revenue/revenueCurrency";
import { RevenueDonutCard } from "./revenue/RevenueDonutCard";
import { RevenueKpiCards } from "./revenue/RevenueKpiCards";
import { RevenueStackedAreaChart } from "./revenue/RevenueStackedAreaChart";

export const RevenueTab: React.FC = () => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();

  const seasonalitySeries = useMemo(
    () => [
      {
        key: "guestServices" as const,
        label: t("analytics.revenue.segments.guestServices", "Guest Services"),
        color: REVENUE_COLORS.guestServices,
      },
      {
        key: "guesteriaPartners" as const,
        label: t(
          "analytics.revenue.segments.guesteriaPartners",
          "Guesteria partners",
        ),
        color: REVENUE_COLORS.guesteriaPartners,
      },
      {
        key: "externalServices" as const,
        label: t(
          "analytics.revenue.segments.externalServices",
          "External services",
        ),
        color: REVENUE_COLORS.externalServices,
      },
    ],
    [t],
  );

  const revenue = useRevenueAnalytics(dateRange);

  const availableCurrencies = useMemo(
    () =>
      collectRevenueCurrencies(
        revenue.total.data,
        revenue.avgCheck.data,
        revenue.distribution.data,
      ),
    [revenue.total.data, revenue.avgCheck.data, revenue.distribution.data],
  );

  const [selectedCurrency, setSelectedCurrency] = useState<
    string | undefined
  >();

  useEffect(() => {
    setSelectedCurrency((current) =>
      resolveRevenueCurrency(availableCurrencies, current),
    );
  }, [availableCurrencies]);

  const displayCurrency = resolveRevenueCurrency(
    availableCurrencies,
    selectedCurrency,
  );

  const kpiMetrics = useMemo(
    () =>
      mapRevenueKpiMetrics(
        revenue.total.data,
        revenue.avgCheck.data,
        revenue.previousTotal.data,
        revenue.previousAvgCheck.data,
        displayCurrency,
        revenue.isReady &&
          revenue.previousRange !== null &&
          !revenue.isComparisonLoading,
      ),
    [
      revenue.total.data,
      revenue.avgCheck.data,
      revenue.previousTotal.data,
      revenue.previousAvgCheck.data,
      revenue.isReady,
      revenue.previousRange,
      revenue.isComparisonLoading,
      displayCurrency,
    ],
  );

  const distributionSegments = useMemo(
    () =>
      mapRevenueDistributionSegments(
        revenue.distribution.data,
        displayCurrency,
      ),
    [revenue.distribution.data, displayCurrency],
  );

  const seasonalityData = useMemo(
    () =>
      mapRevenueSeasonalitySeries(revenue.distribution.data, displayCurrency),
    [revenue.distribution.data, displayCurrency],
  );

  const totalDistribution = distributionSegments.reduce(
    (sum, segment) => sum + segment.value,
    0,
  );

  const totalLabelDistribution = formatRevenueCenterLabel(
    totalDistribution,
    displayCurrency,
  );

  const isExportDisabled = !revenue.isReady || revenue.isLoading;
  const showCurrencySelector = availableCurrencies.length > 1;

  const exportDistribution = useCallback(
    async (format: "csv" | "xlsx") => {
      if (!revenue.isReady) return;
      try {
        const blob = await analyticsRevenueService.exportDistribution({
          hotelId: revenue.hotelId,
          from: revenue.from,
          to: revenue.to,
          format,
        });
        downloadBlob(
          blob,
          `revenue-distribution-${revenue.hotelId}-${revenue.from}-${revenue.to}.${format}`,
        );
        toast.success(
          t(
            "analytics.revenue.toast.distributionExportSuccess",
            "Revenue distribution export downloaded",
          ),
        );
      } catch {
        toast.error(
          t(
            "analytics.revenue.toast.distributionExportError",
            "Failed to export revenue distribution",
          ),
        );
      }
    },
    [revenue.from, revenue.hotelId, revenue.isReady, revenue.to, t],
  );

  const exportRegistration = useMemo(
    () => ({
      canExport: !isExportDisabled,
      onExport: exportDistribution,
    }),
    [exportDistribution, isExportDisabled],
  );
  useRegisterAnalyticsTabExport(exportRegistration);

  return (
    <section className="space-y-6">
      {showCurrencySelector ? (
        <div className="flex items-center justify-end gap-2">
          <span className="text-muted-foreground text-sm">
            {t("analytics.revenue.currency", "Currency")}
          </span>
          <Select
            value={displayCurrency}
            onValueChange={setSelectedCurrency}
            disabled={revenue.isLoading}
          >
            <SelectTrigger className="w-[120px]" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableCurrencies.map((code) => (
                <SelectItem key={code} value={code}>
                  {code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <RevenueKpiCards
        metrics={kpiMetrics}
        isLoading={revenue.isLoading}
        isComparisonLoading={revenue.isComparisonLoading}
      />
      <RevenueDonutCard
        title={t("analytics.revenue.distribution", "Revenue distribution")}
        segments={distributionSegments}
        totalLabel={totalLabelDistribution}
        currency={displayCurrency}
        onExport={exportDistribution}
        exportDisabled={isExportDisabled}
      />
      <RevenueStackedAreaChart
        title={t("analytics.revenue.seasonality", "Seasonality")}
        data={seasonalityData}
        series={seasonalitySeries}
        currency={displayCurrency}
        showLegend
        onExport={exportDistribution}
        exportDisabled={isExportDisabled}
      />

      {/* Future: Revenue by channel — donut + export */}
      {/* Future: Trend by channel — stacked area + export */}
    </section>
  );
};
